<template>
  <div :class="wrapperClasses">
    <label :for="radioId" class="ui-radio__label" @click="handleLabelClick">
      <input
        :id="radioId"
        type="radio"
        :name="name"
        :value="value"
        :checked="isChecked"
        :disabled="disabled"
        :required="required"
        :class="radioClasses"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
      />
      <span class="ui-radio__circle">
        <span v-if="isChecked" class="ui-radio__dot"></span>
      </span>
      <span v-if="label" class="ui-radio__text">{{ label }}</span>
    </label>
    <div v-if="error || helperText" class="ui-radio__helper">
      <span v-if="error" class="ui-radio__error">{{ error }}</span>
      <span v-else-if="helperText" class="ui-radio__helper-text">{{ helperText }}</span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import type { Size } from "./types";

interface Props {
  modelValue?: string | number | boolean;
  value: string | number | boolean;
  name?: string;
  label?: string;
  size?: Size;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  helperText?: string;
  id?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: "md",
  disabled: false,
  required: false,
});

const emit = defineEmits<{
  "update:modelValue": [value: string | number | boolean];
  blur: [event: FocusEvent];
  focus: [event: FocusEvent];
  change: [value: string | number | boolean];
}>();

const isFocused = ref(false);
const radioId = computed(() => props.id || `radio-${Math.random().toString(36).substr(2, 9)}`);

const isChecked = computed(() => props.modelValue === props.value);

const wrapperClasses = computed(() => {
  const classes = ["ui-radio"];
  classes.push(`ui-radio--${props.size}`);
  if (props.disabled) classes.push("ui-radio--disabled");
  if (props.error) classes.push("ui-radio--error");
  return classes;
});

const radioClasses = computed(() => {
  const classes = ["ui-radio__input"];
  if (isFocused.value) classes.push("ui-radio__input--focused");
  return classes;
});

const handleChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.checked) {
    emit("update:modelValue", props.value);
    emit("change", props.value);
  }
};

const handleBlur = (event: FocusEvent) => {
  isFocused.value = false;
  emit("blur", event);
};

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true;
  emit("focus", event);
};

const handleLabelClick = (event: MouseEvent) => {
  if (props.disabled) {
    event.preventDefault();
  }
};
</script>

<style scoped>
.ui-radio {
  display: flex;
  flex-direction: column;
  width: fit-content;
}

.ui-radio__label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  color: #344054;
  user-select: none;
}

.ui-radio--disabled .ui-radio__label {
  cursor: not-allowed;
  opacity: 0.5;
}

.ui-radio__input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.ui-radio__circle {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: var(--color-bg-white);
  border: 2px solid var(--color-border);
  border-radius: 50%;
  transition: all var(--transition-normal);
  flex-shrink: 0;
}

.ui-radio__input:checked + .ui-radio__circle {
  border-color: var(--color-primary);
}

.ui-radio__input:focus + .ui-radio__circle {
  outline: none !important;
  box-shadow: var(--ring) !important;
}

.ui-radio__input:hover:not(:disabled) + .ui-radio__circle {
  border-color: var(--color-text-muted);
}

.ui-radio--disabled .ui-radio__circle {
  background-color: var(--color-bg-disabled);
  border-color: var(--color-border-light);
  cursor: not-allowed;
}

.ui-radio__dot {
  width: 8px;
  height: 8px;
  background-color: var(--color-primary);
  border-radius: 50%;
  transition: all var(--transition-normal);
}

.ui-radio__text {
  font-size: 10px;
  font-weight: 400;
  color: #344054;
}

.ui-radio__helper {
  margin-top: var(--space-1);
  font-size: var(--font-size-xs);
}

.ui-radio__error {
  color: var(--color-danger);
}

.ui-radio__helper-text {
  color: var(--color-text-placeholder);
}
</style>
